import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { Vehicle, VehicleStatus } from "@/types";
import {
  useCommandTypes,
  useSendRawCommand,
  useLatestCommandResult,
  useGt06Commands,
  useSmsAccountStatus,
  useSendSmsCommand,
  useVehicleMetadata,
  useBlockCommandSmsFallback,
} from "@/hooks/queries";
import type { Gt06Command } from "@/services/gt06-commands.service";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { sendBlockCommandWithSmsFallback } from "@/lib/send-block-command-with-sms-fallback";
import { usePendingBlockCommand } from "@/lib/pending-block-commands";
import { isVehicleBlocked } from "@/lib/vehicle-state";
import { VehicleBlockConfirmDialog } from "@/components/vehicle-block-confirm-dialog";
import { usePermission } from "@/hooks/use-permission";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  CUSTOM_COMMAND_TYPE,
  commandDescription,
  commandLabel,
  commandParams,
  defaultAttributesFor,
} from "@/lib/traccar-commands";
import { ApiError } from "@/lib/api/client";
import { Loader2, Radio as RadioIcon, MessageSquare, Lock, Unlock, PenLine } from "lucide-react";
import { LicensePlate } from "@/components/license-plate";

// Atalhos pedidos 2026-09-01 ("já ter os atalhos bloquear desbloquear
// comandos personalizado") — promove os 2 comandos mais usados pra botão
// direto em vez de precisar abrir o Select toda vez; só aparece o atalho
// se o protocolo do dispositivo realmente suportar aquele tipo (ver
// visibleTypes mais abaixo), nunca mostra um atalho que vai falhar ao
// clicar. "Comando personalizado" sempre aparece.
const QUICK_SHORTCUTS: { type: string; label: string; icon: typeof Lock }[] = [
  { type: "engineStop", label: "Bloquear", icon: Lock },
  { type: "engineResume", label: "Desbloquear", icon: Unlock },
];

// Janela de espera pela resposta real do rastreador (commandResult) depois
// de um envio bem-sucedido — ver useLatestCommandResult. GT06 e protocolos
// parecidos costumam responder em poucos segundos quando o device está
// online; 45s cobre isso sem deixar o popup preso esperando pra sempre.
const COMMAND_RESULT_WAIT_MS = 45_000;

const statusLabel = (s: VehicleStatus) =>
  s === "online" ? "Em movimento" : s === "idle" ? "Parado" : "Offline";

type Channel = "gprs" | "sms";
type Attributes = Record<string, string | number | boolean>;

export function VehicleSendCommandDialog({
  vehicle,
  open,
  onOpenChange,
}: {
  vehicle: Vehicle | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const deviceId = vehicle ? Number(vehicle.id) : undefined;
  const { data: types, isLoading: loadingTypes } = useCommandTypes(open ? deviceId : undefined);
  const { data: smsAccount } = useSmsAccountStatus(open);
  // Número SIM (Editar Veículo > Hardware e Simcard) — é o que o backend
  // usa pra endereçar o SMS via Veye, NUNCA o Device.phone do Traccar (não
  // preenchido em nenhum dispositivo desta implantação).
  const { data: vehicleMetadata } = useVehicleMetadata(open ? vehicle?.id : undefined);
  const sendCommand = useSendRawCommand();
  const sendSmsCommand = useSendSmsCommand();
  // Fallback SMS automático pra Bloquear/Desbloquear (pedido 2026-09-06) —
  // mesmo mecanismo do painel desktop/PWA, ver
  // send-block-command-with-sms-fallback.ts. Protocolo vem do cadastro GPS
  // Drive (vehicle_metadata.protocol), nunca de vehicle.protocol
  // (auto-detectado pelo Traccar).
  const smsFallback = useBlockCommandSmsFallback(vehicleMetadata?.protocol, vehicleMetadata?.trackerModel);
  // "Aguardando confirmação" compartilhado com painel do mapa/PWA — em
  // equipamento só-SMS (J4) fica até o rastreador reportar o bloqueio.
  const pendingBlock = usePendingBlockCommand(vehicle?.id, vehicle ? isVehicleBlocked(vehicle) : undefined);
  const [blockFallbackPending, setBlockFallbackPending] = useState(false);
  // Catálogo próprio (Administração > Conta & Integrações > Comandos GT06,
  // pedido 2026-09-01). Desde 2026-09-07 cada comando declara um protocolo
  // e só aparece pra veículos cadastrados com o MESMO protocolo (revertendo
  // a decisão anterior de "não travar por protocolo" — mandar comando do
  // protocolo errado é risco real).
  const { data: allGt06Commands } = useGt06Commands(open);
  const gt06Commands = allGt06Commands?.filter((c) => c.protocol === vehicleMetadata?.protocol);

  const [channel, setChannel] = useState<Channel>("gprs");
  const [selectedType, setSelectedType] = useState<string>("");
  // Tipo do último envio (os atalhos Bloquear/Desbloquear não passam por
  // selectedType) — só pra mensagem de "reenviando por SMS".
  const [lastSentType, setLastSentType] = useState<string>("");
  const [attributes, setAttributes] = useState<Attributes>({});
  const [customText, setCustomText] = useState("");
  // Id do comando GT06 selecionado (ver handleSelectGt06Command) — nulo
  // quando o texto foi digitado à mão. Enviado ao backend no canal SMS pra
  // ele mesmo montar "SENHA COMANDO" (não confiar no customText já
  // concatenado no frontend, que existe só como preview visual/fluxo GPRS).
  const [selectedGt06CommandId, setSelectedGt06CommandId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Popup dedicado de Bloquear/Desbloquear (pedido 2026-09-16) — separado do
  // confirmOpen genérico acima, que continua servindo pra todo o resto dos
  // comandos.
  const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  // Instante do último envio bem-sucedido — dispara o polling de
  // useLatestCommandResult (ver comentário lá) e é limpo no timeout/quando
  // uma resposta chega, o que parar de reconsultar o servidor à toa.
  const [sentAt, setSentAt] = useState<string | undefined>(undefined);
  const [awaitingResult, setAwaitingResult] = useState(false);

  // Reseta o formulário sempre que o popup é reaberto para um veículo.
  useEffect(() => {
    if (!open) return;
    setChannel("gprs");
    setSelectedType("");
    setAttributes({});
    setCustomText("");
    setSelectedGt06CommandId(null);
    setResult(null);
    setSentAt(undefined);
    setAwaitingResult(false);
  }, [open, vehicle?.id]);

  const commandResultQuery = useLatestCommandResult(vehicle?.id, sentAt, awaitingResult);
  useEffect(() => {
    if (commandResultQuery.data) setAwaitingResult(false);
  }, [commandResultQuery.data]);
  useEffect(() => {
    if (!awaitingResult) return;
    const timer = setTimeout(() => setAwaitingResult(false), COMMAND_RESULT_WAIT_MS);
    return () => clearTimeout(timer);
  }, [awaitingResult, sentAt]);

  const gprsSupported = (types ?? []).length > 0;
  // SMS agora é 100% backend GPS Drive → Veye (nunca mais Traccar) — a
  // disponibilidade depende do saldo/ativação da empresa E do "Número SIM"
  // cadastrado em Editar Veículo > Hardware e Simcard (NÃO do
  // server.textEnabled/Device.phone do Traccar, que nunca é preenchido).
  const smsSupported = !!smsAccount?.enabled && !!vehicleMetadata?.simNumber;

  // Equipamento só suporta um dos dois canais: trava o outro automaticamente.
  useEffect(() => {
    if (!gprsSupported && smsSupported) setChannel("sms");
    else if (gprsSupported && !smsSupported) setChannel("gprs");
  }, [gprsSupported, smsSupported]);

  // Canal SMS não usa a lista de tipos nativos do Traccar — só os atalhos
  // GT06 (renderizados separadamente acima) + "Comando personalizado"
  // (SelectItem fixo abaixo).
  // Bloquear/Desbloquear (engineStop/engineResume) exigem devices.lock /
  // devices.unlock especificamente — a mesma checagem já usada no drawer
  // desktop (routes/index.tsx) e no PWA (mobile-app.tsx). Sem isso, um
  // usuário com permissão só de "commands.send_command" (que dá acesso a
  // este popup) via o atalho do Início conseguia ver e usar o atalho de
  // bloqueio mesmo sem a permissão específica de bloquear/desbloquear —
  // filtra aqui pra cobrir tanto os atalhos quanto o Select genérico de
  // comandos de uma vez (ambos derivam de visibleTypes).
  const canLock = usePermission("devices", "lock");
  const canUnlock = usePermission("devices", "unlock");
  const visibleTypes = (channel === "sms" ? [] : (types ?? [])).filter((t) => {
    if (t.type === "engineStop") return canLock;
    if (t.type === "engineResume") return canUnlock;
    return true;
  });
  const isCustom = selectedType === CUSTOM_COMMAND_TYPE;
  const params = selectedType ? commandParams(selectedType) : [];
  const availableShortcuts = QUICK_SHORTCUTS.filter((s) =>
    visibleTypes.some((t) => t.type === s.type),
  );

  const handleSelectType = (type: string) => {
    setSelectedType(type);
    setAttributes(defaultAttributesFor(type));
    setCustomText("");
    setSelectedGt06CommandId(null);
  };

  // Comando do catálogo GT06 (ver useGt06Commands acima) — sempre vai como
  // comando personalizado (o Traccar não tem um "type" próprio pra texto
  // arbitrário do protocolo), com a senha cadastrada na frente do texto
  // quando existir. Formato "SENHA COMANDO" é o mais comum pros comandos
  // GT06 por SMS — se o equipamento exigir outro separador, o texto do
  // comando já cadastrado pode incluir isso. Guarda também o id (ver
  // selectedGt06CommandId) para o canal SMS mandar ao backend, que
  // reconstrói o texto com a senha correta em vez de depender deste preview
  // já concatenado.
  const handleSelectGt06Command = (cmd: Gt06Command) => {
    setSelectedType(CUSTOM_COMMAND_TYPE);
    setAttributes({});
    setSelectedGt06CommandId(cmd.id);
    setCustomText(cmd.password ? `${cmd.password} ${cmd.commandText}` : cmd.commandText);
  };

  const buildAttributes = (): Record<string, unknown> => {
    if (isCustom) return { data: customText };
    return attributes;
  };

  const canSend =
    !!vehicle &&
    !!selectedType &&
    (!isCustom || customText.trim().length > 0) &&
    !sendCommand.isPending &&
    !sendSmsCommand.isPending &&
    !blockFallbackPending;

  // Pedido 2026-09-16: o popup dedicado de Bloquear/Desbloquear chama isso
  // direto (sem passar por "selecionar tipo → clicar Enviar"), então aceita
  // um `type` explícito em vez de só ler `selectedType` do closure — ler só
  // o state aqui daria valor obsoleto, já que o clique que abre o popup e o
  // clique que confirma são o mesmo `handleSelectType` + disparo imediato,
  // sem um render de sobra no meio pra o state assentar.
  const handleConfirmSend = (typeOverride?: string) => {
    const type = typeOverride ?? selectedType;
    if (!vehicle || !deviceId || !type) return;
    setLastSentType(type);
    setConfirmOpen(false);
    setBlockConfirmOpen(false);
    setResult(null);

    // Bloquear/Desbloquear via GPRS ganham o mesmo fallback SMS automático
    // do painel desktop/PWA (offline ou erro do Traccar → tenta SMS antes
    // de desistir) — qualquer outro tipo de comando continua no caminho
    // normal (sendCommand.mutate) mais abaixo.
    if (channel === "gprs" && (type === "engineStop" || type === "engineResume")) {
      setBlockFallbackPending(true);
      sendBlockCommandWithSmsFallback({
        vehicleId: vehicle.id,
        action: type === "engineStop" ? "block" : "unblock",
        offline: vehicle.status === "offline",
        fallback: smsFallback,
      })
        .then((outcome) => {
          if (outcome.channel === "none") {
            const desc =
              outcome.reason === "offline"
                ? "Veículo offline (sem comunicação com o rastreador agora)."
                : outcome.error instanceof Error
                  ? outcome.error.message
                  : undefined;
            toast.error("Erro ao enviar comando", { description: desc });
            setResult({ ok: false, message: `❌ ${desc || "Erro retornado pelo servidor."}` });
            return;
          }
          const msg = `${commandLabel(type)} — ${vehicle.name}`;
          if (outcome.channel === "sms") {
            const why = smsFallback.smsOnly ? "este equipamento só bloqueia por SMS" : "GPRS indisponível";
            toast.success("Comando enviado por SMS", { description: `${msg} (${why})` });
            setResult({ ok: true, message: `✅ Comando enviado por SMS (${why}).` });
          } else {
            toast.success("Comando enviado com sucesso", { description: msg });
            setResult({ ok: true, message: "✅ Comando enviado com sucesso." });
            setSentAt(new Date().toISOString());
            setAwaitingResult(true);
          }
        })
        .catch((err) => {
          const desc = err instanceof Error ? err.message : undefined;
          toast.error("Erro ao enviar comando", { description: desc });
          setResult({ ok: false, message: `❌ ${desc || "Erro retornado pelo servidor."}` });
        })
        .finally(() => setBlockFallbackPending(false));
      return;
    }

    if (channel === "sms") {
      // Backend GPS Drive → Veye, nunca o Traccar. Sem
      // commandResult/polling nesse canal — a Veye não expõe status de
      // entrega (ver SmsSendService no backend).
      sendSmsCommand.mutate(
        selectedGt06CommandId ? { deviceId, gt06CommandId: selectedGt06CommandId } : { deviceId, customText },
        {
          onSuccess: () => {
            toast.success("SMS enviado com sucesso", { description: vehicle.name });
            setResult({ ok: true, message: "✅ SMS enviado com sucesso." });
          },
          onError: (err) => {
            const desc = err instanceof GpsDriveApiError ? err.message : err instanceof Error ? err.message : undefined;
            toast.error("Erro ao enviar SMS", { description: desc });
            setResult({ ok: false, message: `❌ ${desc || "Erro retornado pelo servidor."}` });
          },
        },
      );
      return;
    }

    sendCommand.mutate(
      { deviceId, type, attributes: buildAttributes(), textChannel: false },
      {
        onSuccess: () => {
          const msg = `${commandLabel(type)} — ${vehicle.name}`;
          toast.success("Comando enviado com sucesso", { description: msg });
          setResult({ ok: true, message: "✅ Comando enviado com sucesso." });
          // "Tem que retornar o que o rastreador disse" — só o Traccar ter
          // aceitado o envio não significa que o equipamento executou; fica
          // de olho num commandResult novo por um tempo (ver
          // useLatestCommandResult/COMMAND_RESULT_WAIT_MS).
          setSentAt(new Date().toISOString());
          setAwaitingResult(true);
        },
        onError: (err) => {
          const desc = err instanceof ApiError ? err.message : err instanceof Error ? err.message : undefined;
          toast.error("Erro ao enviar comando", { description: desc });
          setResult({ ok: false, message: `❌ ${desc || "Erro retornado pelo servidor."}` });
        },
      },
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* Conteúdo é longo (atalhos + catálogo GT06 + campos dinâmicos +
            resposta do rastreador) — sem max-h/overflow o Dialog base
            (ui/dialog.tsx) centraliza vertical sem limite de altura, e num
            celular isso empurra o cabeçalho (com o X de fechar) pra fora do
            topo da tela (bug relatado 2026-09-07: "preencheu toda a tela e
            não tem como fechar"). Mesmo padrão já usado em outros dialogs
            grandes (routes/index.tsx, routes/users.tsx). */}
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Enviar comando</DialogTitle>
            <DialogDescription>
              Comandos reais consultados diretamente no servidor, conforme o protocolo do dispositivo.
            </DialogDescription>
          </DialogHeader>

          {!vehicle ? (
            <div className="text-sm text-muted-foreground">Selecione um veículo.</div>
          ) : (
            <div className="space-y-4">
              {/* Veículo */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-lg border border-border bg-muted/40 p-3 text-xs">
                <div>
                  <div className="text-muted-foreground">Nome</div>
                  <div className="font-semibold text-foreground">{vehicle.name}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Placa</div>
                  <div><LicensePlate plate={vehicle.plate} size="sm" /></div>
                </div>
                <div>
                  <div className="text-muted-foreground">IMEI</div>
                  <div className="font-mono font-semibold text-foreground">{vehicle.imei ?? "—"}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Status</div>
                  <div className="font-semibold text-foreground">{statusLabel(vehicle.status)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Protocolo</div>
                  <div className="font-mono font-semibold text-foreground">
                    {vehicle.protocol ?? "—"}
                  </div>
                </div>
              </div>

              {/* Atalhos — pedido 2026-09-01, só aparecem se o protocolo
                  deste dispositivo realmente suportar o comando no canal
                  escolhido (ver availableShortcuts). */}
              {availableShortcuts.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {availableShortcuts.map((s) => (
                    <button
                      key={s.type}
                      type="button"
                      onClick={() => {
                        handleSelectType(s.type);
                        // Pedido 2026-09-16 ("no atalho de bloquear quero
                        // que abra o popup também") — Bloquear/Desbloquear
                        // pulam o fluxo normal (selecionar → clicar Enviar →
                        // confirmação genérica) e vão direto pro popup
                        // dedicado, igual ao PWA e ao painel desktop.
                        if (s.type === "engineStop" || s.type === "engineResume") {
                          setBlockConfirmOpen(true);
                        }
                      }}
                      className={cn(
                        "flex flex-col items-center gap-1 rounded-lg border p-2.5 text-xs font-semibold transition-colors",
                        selectedType === s.type
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border text-foreground hover:bg-accent",
                      )}
                    >
                      <s.icon className="h-4 w-4" />
                      {s.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => handleSelectType(CUSTOM_COMMAND_TYPE)}
                    className={cn(
                      "flex flex-col items-center gap-1 rounded-lg border p-2.5 text-xs font-semibold transition-colors",
                      isCustom
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-foreground hover:bg-accent",
                    )}
                  >
                    <PenLine className="h-4 w-4" />
                    Personalizado
                  </button>
                </div>
              )}

              {/* Comandos GT06 — pedido 2026-09-01, lista separada dos
                  atalhos padrão do sistema (cadastrados em Administração >
                  Conta & Integrações > Comandos GT06, por empresa). Desde
                  2026-09-07, só mostra os comandos do MESMO protocolo
                  cadastrado neste veículo (ver filtro de gt06Commands
                  acima). */}
              {!vehicleMetadata?.protocol ? (
                <p className="text-[11px] text-muted-foreground">
                  Protocolo do rastreador não cadastrado — edite o veículo (Hardware e Simcard) para
                  ver os comandos do catálogo disponíveis para ele.
                </p>
              ) : (
                !!gt06Commands?.length && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Comandos GT06
                  </Label>
                  <div className="space-y-1.5">
                    {gt06Commands.map((cmd) => {
                      const active = isCustom && customText === (cmd.password ? `${cmd.password} ${cmd.commandText}` : cmd.commandText);
                      return (
                        <button
                          key={cmd.id}
                          type="button"
                          onClick={() => handleSelectGt06Command(cmd)}
                          className={cn(
                            "flex w-full items-center justify-between gap-2 rounded-lg border p-2.5 text-left text-xs transition-colors",
                            active
                              ? "border-primary bg-primary/10"
                              : "border-border hover:bg-accent",
                          )}
                        >
                          <div className="min-w-0">
                            <div className="font-semibold text-foreground">{cmd.name}</div>
                            {cmd.description && (
                              <div className="truncate text-muted-foreground">{cmd.description}</div>
                            )}
                          </div>
                          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                            {cmd.commandText}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                )
              )}

              {/* Tipo de envio */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Tipo de envio
                </Label>
                <RadioGroup
                  value={channel}
                  onValueChange={(v) => setChannel(v as Channel)}
                  className="grid grid-cols-2 gap-2"
                >
                  <label
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-md border p-2.5 text-sm font-medium transition-colors",
                      !gprsSupported && "cursor-not-allowed opacity-50",
                      channel === "gprs" ? "border-primary bg-primary/10" : "border-border hover:bg-accent",
                    )}
                  >
                    <RadioGroupItem value="gprs" disabled={!gprsSupported} />
                    <RadioIcon className="h-4 w-4" /> GPRS
                  </label>
                  <label
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-md border p-2.5 text-sm font-medium transition-colors",
                      !smsSupported && "cursor-not-allowed opacity-50",
                      channel === "sms" ? "border-primary bg-primary/10" : "border-border hover:bg-accent",
                    )}
                  >
                    <RadioGroupItem value="sms" disabled={!smsSupported} />
                    <MessageSquare className="h-4 w-4" /> SMS
                  </label>
                </RadioGroup>
                {!smsSupported && (
                  <p className="text-[11px] text-muted-foreground">
                    {!smsAccount?.enabled
                      ? "SMS indisponível: envio de SMS não está ativado para esta empresa."
                      : "SMS indisponível: este veículo não tem \"Número SIM\" cadastrado (aba Hardware e Simcard)."}
                  </p>
                )}
              </div>

              <Separator />

              {/* Lista de comandos (dinâmica, real) */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Comando
                </Label>
                {loadingTypes ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Carregando comandos suportados…
                  </div>
                ) : (
                  <Select value={selectedType} onValueChange={handleSelectType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um comando" />
                    </SelectTrigger>
                    <SelectContent>
                      {visibleTypes
                        .filter((t) => t.type !== CUSTOM_COMMAND_TYPE)
                        .map((t) => (
                          <SelectItem key={t.type} value={t.type}>
                            {commandLabel(t.type)}
                          </SelectItem>
                        ))}
                      <SelectItem value={CUSTOM_COMMAND_TYPE}>📝 Comando personalizado</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {!loadingTypes && visibleTypes.length === 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    Nenhum comando suportado pelo protocolo deste dispositivo neste canal.
                  </p>
                )}
              </div>

              {/* Campo comando: nome, descrição e parâmetros */}
              {selectedType && (
                <div className="space-y-3 rounded-lg border border-border p-3">
                  <div>
                    <div className="text-sm font-bold text-foreground">
                      {isCustom ? "📝 Comando personalizado" : commandLabel(selectedType)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {isCustom
                        ? "Envie um texto bruto diretamente no protocolo do rastreador."
                        : commandDescription(selectedType)}
                    </div>
                  </div>

                  {isCustom ? (
                    <div className="space-y-1">
                      <Label htmlFor="custom-command-text" className="text-xs">Comando</Label>
                      <Textarea
                        id="custom-command-text"
                        value={customText}
                        onChange={(e) => {
                          setCustomText(e.target.value);
                          setSelectedGt06CommandId(null);
                        }}
                        placeholder={"AT+GTFRI=...\nou\nbegin123456"}
                        rows={4}
                        className="font-mono text-xs"
                      />
                    </div>
                  ) : (
                    params.length > 0 && (
                      <div className="grid grid-cols-2 gap-3">
                        {params.map((p) => (
                          <div key={p.key} className="space-y-1">
                            <Label htmlFor={`param-${p.key}`} className="text-xs">{p.label}</Label>
                            {p.type === "boolean" ? (
                              <div className="flex h-9 items-center">
                                <Checkbox
                                  id={`param-${p.key}`}
                                  checked={!!attributes[p.key]}
                                  onCheckedChange={(checked) =>
                                    setAttributes((a) => ({ ...a, [p.key]: checked === true }))
                                  }
                                />
                              </div>
                            ) : (
                              <Input
                                id={`param-${p.key}`}
                                type={p.type === "number" ? "number" : "text"}
                                value={String(attributes[p.key] ?? "")}
                                onChange={(e) =>
                                  setAttributes((a) => ({
                                    ...a,
                                    [p.key]: p.type === "number" ? Number(e.target.value) : e.target.value,
                                  }))
                                }
                                className="h-9"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>
              )}

              {/* Histórico — aguardando Backend GPS Drive */}
              {/* <CommandHistoryList vehicleId={vehicle.id} /> */}

              {result && (
                <div
                  className={cn(
                    "rounded-md border px-3 py-2 text-xs font-semibold",
                    result.ok
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                      : "border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400",
                  )}
                >
                  {result.message}
                </div>
              )}

              {/* "O que o rastreador disse" — pedido 2026-09-01: resposta
                  crua devolvida pelo protocolo (ex.: GT06
                  "RELAY,1#=Success!"), não só a confirmação de que o
                  Traccar aceitou o envio. Traccar não relaciona o evento
                  commandResult a qual comando o originou (só ao device/
                  horário) — por isso "mais recente depois do envio", nunca
                  uma correlação garantida 1:1, ver useLatestCommandResult. */}
              {pendingBlock?.channel === "sms" && (
                <div className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                  Aguardando o rastreador confirmar o {pendingBlock.action === "block" ? "bloqueio" : "desbloqueio"} (SMS
                  enviado às {new Date(pendingBlock.sentAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}).
                  Pode fechar esta janela.
                </div>
              )}

              {channel === "gprs" && result?.ok && !(pendingBlock?.channel === "sms") && (
                <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs">
                  <div className="mb-1 font-semibold uppercase tracking-wide text-muted-foreground">
                    Resposta do rastreador
                  </div>
                  {commandResultQuery.data ? (
                    <p className="font-mono text-foreground">
                      {commandResultQuery.data.result?.trim() || "(confirmação recebida, sem texto)"}
                    </p>
                  ) : awaitingResult ? (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Aguardando o rastreador responder…
                    </div>
                  ) : (
                    <p className="text-muted-foreground">
                      {smsFallback.smsEnabled &&
                      ((lastSentType === "engineStop" && smsFallback.blockGt06CommandId) ||
                        (lastSentType === "engineResume" && smsFallback.unblockGt06CommandId))
                        ? "Rastreador não confirmou pelo GPRS — reenviando por SMS."
                        : "Sem resposta do rastreador até agora — confira depois na aba Logs."}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
            <Button disabled={!canSend} onClick={() => setConfirmOpen(true)}>
              {sendCommand.isPending || sendSmsCommand.isPending || blockFallbackPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Enviar"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deseja realmente enviar este comando?</AlertDialogTitle>
            <AlertDialogDescription>
              {vehicle && selectedType && (
                <>
                  <strong>{isCustom ? "Comando personalizado" : commandLabel(selectedType)}</strong> será
                  enviado para <strong>{vehicle.name}</strong> via {channel === "sms" ? "SMS" : "GPRS"}.
                  Esta ação envia um comando real ao rastreador.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleConfirmSend()}>Enviar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {vehicle && (
        <VehicleBlockConfirmDialog
          open={blockConfirmOpen}
          onOpenChange={setBlockConfirmOpen}
          vehicleName={vehicle.name}
          vehiclePlate={vehicle.plate}
          canLock={canLock}
          canUnlock={canUnlock}
          sending={blockFallbackPending}
          onConfirm={(action) =>
            handleConfirmSend(action === "block" ? "engineStop" : "engineResume")
          }
        />
      )}
    </>
  );
}
